import { CarRentalResponse } from '@/types/benefits/carRental/CarRentalResponse';
import { CURRENCIES } from '../../../../../shared/CountriesData';
import { WEEKDAYS } from '@/constants/weekdays';
import { SupportedLocale } from '@/types/supported-locale.type';
import { LOCALES } from '@/i18n/config';
import { numberIfNotUndefined } from '../../prices/numberIfNotUndefined';

const initializeCarRentalTranslations = (carRental: CarRentalResponse) => {
  const translations = {} as Record<
    SupportedLocale,
    {
      benefit?: { name?: string };
      carCategories?: {
        category?: string;
        model?: string;
        description?: string;
        included?: string;
        notIncluded?: string;
        insurances?: {
          name?: string;
          shortDescription?: string;
          longDescription?: string;
        }[];
        taxes?: { name?: string }[];
        options?: { name?: string; description?: string }[];
        oneWayFees?: { name?: string; description?: string }[];
      }[];
      insurances?: {
        name?: string;
        shortDescription?: string;
        longDescription?: string;
      }[];
      taxes?: { name?: string }[];
      options?: { name?: string; description?: string }[];
      oneWayFees?: { name?: string; description?: string }[];
    }
  >;
  for (const locale of LOCALES) {
    translations[locale] = {
      benefit: {
        name:
          carRental.translations?.[locale]?.benefit?.name ??
          carRental.benefit.name ??
          '',
      },
      carCategories: carRental.translations?.[locale]?.carCategories?.map(
        (category) => ({
          category: category.category ?? undefined,
          model: category.model ?? undefined,
          description: category.description ?? undefined,
          included: category.included ?? undefined,
          notIncluded: category.notIncluded ?? undefined,
          insurances: category.insurances?.map((insurance) => ({
            name: insurance.name ?? undefined,
            shortDescription: insurance.shortDescription ?? undefined,
            longDescription: insurance.longDescription ?? undefined,
          })),
          taxes: category.taxes?.map((tax) => ({
            name: tax.name ?? undefined,
          })),
          options: category.options?.map((option) => ({
            name: option.name ?? undefined,
            description: option.description ?? undefined,
          })),
          oneWayFees: category.oneWayFees?.map((fee) => ({
            name: fee.name ?? undefined,
            description: fee.description ?? undefined,
          })),
        }),
      ),
      insurances: carRental.translations?.[locale]?.insurances?.map(
        (insurance) => ({
          name: insurance.name ?? undefined,
          shortDescription: insurance.shortDescription ?? undefined,
          longDescription: insurance.longDescription ?? undefined,
        }),
      ),
      taxes: carRental.translations?.[locale]?.taxes?.map((tax) => ({
        name: tax.name ?? undefined,
      })),
      options: carRental.translations?.[locale]?.options?.map((option) => ({
        name: option.name ?? undefined,
        description: option.description ?? undefined,
      })),
      oneWayFees: carRental.translations?.[locale]?.oneWayFees?.map((fee) => ({
        name: fee.name ?? undefined,
        description: fee.description ?? undefined,
      })),
    };
  }
  return translations;
};

export const loadCarRentalDefaultValues = (carRental: CarRentalResponse) =>
  ({
    translations: initializeCarRentalTranslations(carRental),
    name: carRental.benefit.name,
    supplier: {
      label: carRental.benefit.supplier.name,
      value: carRental.benefit.supplier.id,
    },
    currency: carRental.benefit.currency
      ? {
          value: carRental.benefit.currency,
          label: CURRENCIES.find(
            (currency) => currency.value === carRental.benefit.currency,
          )?.label,
        }
      : undefined,
    country: carRental.benefit.country
      ? {
          label: carRental.benefit.country,
          value: carRental.benefit.country,
        }
      : undefined,
    marginRate: numberIfNotUndefined(carRental.benefit.marginRate),
    displayGlobalForm: carRental.displayGlobalForm ? 'true' : 'false',
    shouldLimitInsurance: carRental.shouldLimitInsurance ? 'true' : 'false',
    insurances:
      carRental.insurances && carRental.insurances.length
        ? carRental.insurances.map((insurance) => ({
            name: insurance.name ?? undefined,
            shortDescription: insurance.shortDescription ?? undefined,
            longDescription: insurance.longDescription ?? undefined,
            amount: insurance.amount ? Number(insurance.amount) : undefined,
            billingType: insurance.billingType ?? undefined,
          }))
        : [{}],
    taxes:
      carRental.taxes && carRental.taxes.length
        ? carRental.taxes.map((tax) => ({
            name: tax.name ?? undefined,
            amount: tax.amount ? Number(tax.amount) : undefined,
            billingType: tax.billingType ?? undefined,
            required: tax.required ?? undefined,
          }))
        : [{}],
    options:
      carRental.options && carRental.options.length
        ? carRental.options.map((option) => ({
            name: option.name ?? undefined,
            description: option.description ?? undefined,
            amount: option.amount ? Number(option.amount) : undefined,
            billingType: option.billingType ?? undefined,
          }))
        : [{}],
    oneWayFees:
      carRental.oneWayFees && carRental.oneWayFees.length
        ? carRental.oneWayFees.map((fee) => ({
            name: fee.name ?? undefined,
            departureCity: fee.departureCity ?? undefined,
            arrivalCity: fee.arrivalCity ?? undefined,
            description: fee.description ?? undefined,
            amount: fee.amount ? Number(fee.amount) : undefined,
          }))
        : [{}],
    carCategories:
      carRental.carCategories && carRental.carCategories.length
        ? carRental.carCategories.map(
            (category) =>
              ({
                category: category.category ?? undefined,
                model: category.model ?? undefined,
                description: category.description ?? undefined,
                images: category.images ? category.images : [],
                maxCapacity: category.maxCapacity
                  ? Number(category.maxCapacity)
                  : undefined,
                maxNbLuggage: category.maxNbLuggage
                  ? Number(category.maxNbLuggage)
                  : undefined,
                gearboxType: category.gearboxType ?? undefined,
                airConditioning: category.airConditioning ? 'true' : 'false',
                includedMileage: category.includedMileage
                  ? Number(category.includedMileage)
                  : undefined,
                isIncludedMileageUnlimited:
                  category.isIncludedMileageUnlimited ?? undefined,
                additionalCost: category.additionalCost
                  ? Number(category.additionalCost)
                  : undefined,
                standardDeductableAmount: category.standardDeductableAmount
                  ? Number(category.standardDeductableAmount)
                  : undefined,
                depositAmount: category.depositAmount
                  ? Number(category.depositAmount)
                  : undefined,
                minimumAge: category.minimumAge
                  ? Number(category.minimumAge)
                  : undefined,
                included: category.included ?? undefined,
                notIncluded: category.notIncluded ?? undefined,
                displayCategoryOptions:
                  category.displayCategoryOptions !== undefined
                    ? category.displayCategoryOptions
                      ? 'true'
                      : 'false'
                    : 'true',
                shouldLimitInsurance: category.shouldLimitInsurance
                  ? 'true'
                  : 'false',
                insurances:
                  category.insurances && category.insurances.length
                    ? category.insurances.map((insurance) => ({
                        name: insurance.name ?? undefined,
                        shortDescription:
                          insurance.shortDescription ?? undefined,
                        longDescription: insurance.longDescription ?? undefined,
                        amount: insurance.amount
                          ? Number(insurance.amount)
                          : undefined,
                        billingType: insurance.billingType ?? undefined,
                      }))
                    : [{}],
                options:
                  category.options && category.options.length
                    ? category.options.map((option) => ({
                        name: option.name ?? undefined,
                        description: option.description ?? undefined,
                        amount: option.amount
                          ? Number(option.amount)
                          : undefined,
                        billingType: option.billingType ?? undefined,
                      }))
                    : [{}],
                taxes:
                  category.taxes && category.taxes.length
                    ? category.taxes.map((tax) => ({
                        name: tax.name ?? undefined,
                        amount: tax.amount ? Number(tax.amount) : undefined,
                        billingType: tax.billingType ?? undefined,
                        required: tax.required ?? undefined,
                      }))
                    : [{}],
                oneWayFees:
                  category.oneWayFees && category.oneWayFees.length
                    ? category.oneWayFees.map((oneWayFee) => ({
                        name: oneWayFee.name ?? undefined,
                        departureCity: oneWayFee.departureCity ?? undefined,
                        arrivalCity: oneWayFee.arrivalCity ?? undefined,
                        description: oneWayFee.description ?? undefined,
                        amount: oneWayFee.amount
                          ? Number(oneWayFee.amount)
                          : undefined,
                      }))
                    : [{}],
                tarifs:
                  category.pricings && category.pricings.length
                    ? category.pricings.map((tarif) => ({
                        name: tarif.name ?? undefined,
                        periods: tarif.periods.map((period) => ({
                          startDate: period.startDate
                            ? new Date(period.startDate)
                            : undefined,
                          endDate: period.endDate
                            ? new Date(period.endDate)
                            : undefined,
                          applicationDays: period.applicationDays,
                        })),
                        pricePartitions: tarif.pricePartitions.map(
                          (partition) => ({
                            price: numberIfNotUndefined(partition.price),
                            from: partition.from
                              ? Number(partition.from)
                              : undefined,
                            to: partition.to ? Number(partition.to) : undefined,
                          }),
                        ),
                      }))
                    : [
                        {
                          periods: [{ applicationDays: WEEKDAYS }],
                          pricePartitions: [{}],
                        },
                      ],
              }) as const,
          )
        : [
            {
              tarifs: [
                {
                  periods: [{}],
                  pricePartitions: [
                    {
                      from: 1,
                      to: 1,
                    },
                  ],
                },
              ],
              insurances: [{}],
              options: [{}],
              taxes: [{}],
            },
          ],
  }) as const;
